import type { JSX } from 'react';
import { Alert, Button, Modal, ModalActions, ModalContent, ModalTitleInfo } from '@c2fo/liquidity';
import percentageRotate from '@/enterprise/assets/percentagerotate.svg?url';
import { isImpersonationSession } from '@/utils/token';
import { useAgreementsUrl } from '@/utils/useAgreementsUrl';
import { GET_A_QUOTE_MODAL_CONTENT } from './content';

interface GetAQuoteModalProps {
  open: boolean;
  onClose: () => void;
  /** Runs the quotes POST. The wizard owns the mutation, so the skip path shares this one call. */
  onAcknowledge: () => void;
}

/**
 * Consent disclosure shown before a quote is created. Pressing the primary button is the
 * acknowledgement SEA records, which is why the POST only happens from here or from a supplier
 * `useMonduHandoffSkip` found had already acknowledged.
 *
 * Deliberately not a mode on `MonduHandoffModal`: that modal discloses leaving the site for Mondu,
 * and this flow stays put.
 */
export const GetAQuoteModal = ({ open, onClose, onAcknowledge }: GetAQuoteModalProps): JSX.Element => {
  const { privacyLink } = useAgreementsUrl();
  const isImpersonating = isImpersonationSession();

  const handleProceed = (): void => {
    if (isImpersonating) {
      return;
    }

    onAcknowledge();
  };

  return (
    <Modal open={open} onClose={onClose} size="sm">
      <ModalTitleInfo>{GET_A_QUOTE_MODAL_CONTENT.title}</ModalTitleInfo>
      <ModalContent center>
        <img src={percentageRotate} alt="" className="size-[70px]" />
        <p>{GET_A_QUOTE_MODAL_CONTENT.disclaimer}</p>
        <p>
          {GET_A_QUOTE_MODAL_CONTENT.consent.beforeLink}
          <a href={privacyLink} target="_blank" rel="noreferrer" className="font-semibold text-blue-500">
            {GET_A_QUOTE_MODAL_CONTENT.consent.privacyPolicyLabel}
          </a>
          {GET_A_QUOTE_MODAL_CONTENT.consent.afterLink}
        </p>
        {isImpersonating && (
          <div className="w-full text-left">
            <Alert type="info" full description={GET_A_QUOTE_MODAL_CONTENT.impersonationMessage} />
          </div>
        )}
      </ModalContent>
      <ModalActions>
        <Button variant="secondary" onClick={onClose}>
          {GET_A_QUOTE_MODAL_CONTENT.cancelLabel}
        </Button>
        <Button variant="cta" onClick={handleProceed} disabled={isImpersonating}>
          {GET_A_QUOTE_MODAL_CONTENT.proceedLabel}
        </Button>
      </ModalActions>
    </Modal>
  );
};
